import { useState, useEffect, useCallback } from 'react';
import type { Deal, DealFormData } from '../types/deal';

// import type { StatusCount } from '../types/status';
import { dealService } from '../services/domain/dealService';

interface StatusCount {
    status_id: number,
    status_name: string,
    deal_count: number,
    process: {
        id: number,
        stage: number
    }
}

interface UseDealsResult {
  data: Deal[];
  dealData: Deal | null;
  statusCount: StatusCount[];
  loading: boolean;
  error: string | null;
  total: number;
  totalPages: number;
  refetch: () => void;
  updating: boolean;
  searching: boolean;
  creating: boolean;


  createDeal: (formData: DealFormData) => Promise<{ success: boolean, message?: string }>;
  searchDealById: (id: number) => Promise<{ success: boolean, message?: string }>;

  // onGenerateQR: (employeeId: string, validationDate: string) => Promise<{ success: boolean, message?: string }>;

}

interface DealParams {
  status_persona_id?: number;
  page?: number;
  name_persona?: string;
  pageSize?: number,
  tipo_persona_id?: string,
  searchTerm?: string;
  agentId?: string;
  sourceId?: string;
  statusId?: string;
}



// interface UserFormResponse {
//   success: boolean,
//   personId: string
// }

export const useDeal = (params: DealParams): UseDealsResult => {
  const [data, setData] = useState<Deal[]>([]);
  const [dealData, setDealData] = useState<Deal | null>(null)
  const [statusCount, setStatusCount] = useState<StatusCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [updating] = useState(false);
  const [searching] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchDeal = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Construir parámetros de búsqueda
      const searchParams: any = {
        page: params.page,
        pageSize: params.pageSize
      };

      // Agregar filtros solo si tienen valor
      if (params.searchTerm) {
        searchParams.searchTerm = params.searchTerm;
      }
      if (params.agentId) {
        searchParams.user_id = params.agentId;
      }
      if (params.sourceId) {
        searchParams.deal_source_id = params.sourceId;
      }
      if (params.statusId) {
        searchParams.deal_status_id = params.statusId;
      }
      if (params.name_persona) {
        searchParams.name_persona = params.name_persona;
      }
      if (params.status_persona_id) {
        searchParams.status_persona_id = params.status_persona_id;
      }
      if (params.tipo_persona_id) {
        searchParams.tipo_persona_id = params.tipo_persona_id;
      }

      const response = await dealService.getDeals(searchParams);
      
      setData(response.deals);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    params.page, 
    params.pageSize,
    params.searchTerm,
    params.agentId,
    params.sourceId,
    params.statusId,
    params.status_persona_id, 
    params.name_persona,
    params.tipo_persona_id
  ]);

  const searchDealById = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);

      const response = await dealService.getDealsbyId(id);
      if(response){
        setDealData(response);
      }
      

      return ({success: true})
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching empdealos:', err);
      return ({success: false})
    } finally {
      setLoading(false);
    }
  }, [fetchDeal]);

  useEffect(() => {
    fetchDeal();
    fetchStatusCount();
  }, [fetchDeal]);

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null);
    }
    catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en actualizar';
      setError(errorMessage);
    } finally {
      setLoading(false)
    }
  }, [fetchDeal]);

  const createDeal = useCallback(async (
    formData: DealFormData
  ) => {
    try {
      setCreating(true);
      setError(null);
    
      const result = await dealService.createDeal(formData);
      if (result.error) {
        setError(`Error al crear deal: ${result.error}`);
        return {
        success: false,
      };
      }

      await fetchDeal();
      await fetchStatusCount();

      return {
        success: true,
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setCreating(false);
    }
  }, [fetchDeal]);

  const fetchStatusCount = useCallback(async () => {
    try {
      setError(null);
      const response = await dealService.getStatusCount();
      setStatusCount(response.status_counts);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching empdealos:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchDeal]);

  return {
    data,
    dealData,
    statusCount,
    loading,
    error,
    total,
    totalPages,
    refetch,
    updating,
    searching,
    creating,
    createDeal,
    searchDealById
  };
};